import { useState, useMemo } from 'react';
import { ArrowDown, MessageSquare, Radio } from 'lucide-react';
import { useAutoScroll } from '@reflex/ui/hooks/useAutoScroll';
import { Button } from '@reflex/ui/components/ui/button';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { EventBubble } from '@reflex/ui/components/stream/EventBubble';
import { AxonEventList } from '@reflex/plugin-axon-viewer/web/AxonStreamView';
import {
  FlowStartedBubble,
  FlowNodeStartedBubble,
  FlowNodeCompletedBubble,
  FlowNodeErrorBubble,
  FlowNodeRetryingBubble,
  FlowApprovalRequestedBubble,
  FlowApprovalRespondedBubble,
  FlowCompletedBubble,
  FlowErrorBubble,
  FlowCancelledBubble,
} from './FlowBubbles';
import { AgentStreamSummaryBubble } from './AgentStreamSummaryBubble';
import { cn } from '@reflex/ui/lib/utils';
import type { AxonEvent } from '@reflex/shared';

type StreamView = 'thread' | 'axon';

function ViewToggle({ view, onChange }: { view: StreamView; onChange: (v: StreamView) => void }) {
  return (
    <div className="flex rounded-full bg-muted/80 p-0.5 backdrop-blur">
      {(['thread', 'axon'] as const).map((v) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={cn(
            'rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors',
            view === v
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

function isFlowEvent(type: string): boolean {
  return type.startsWith('flow.');
}

/**
 * Build a chronological render plan for the thread view.
 *
 * Flow events render in place. Agent (non-flow) events collapse into a
 * single `AgentStreamSummaryBubble` per `streamId`, anchored at the
 * earliest occurrence of any event from that stream so the summary
 * stays interleaved with the surrounding flow events.
 */
interface FlowEntry {
  kind: 'flow';
  event: AxonEvent;
}

interface AgentEntry {
  kind: 'agent';
  streamId: string;
  events: AxonEvent[];
}

type Entry = FlowEntry | AgentEntry;

function buildEntries(events: AxonEvent[]): Entry[] {
  const eventsByStream = new Map<string, AxonEvent[]>();
  for (const event of events) {
    if (isFlowEvent(event.type)) continue;
    let list = eventsByStream.get(event.streamId);
    if (!list) {
      list = [];
      eventsByStream.set(event.streamId, list);
    }
    list.push(event);
  }

  const rendered = new Set<string>();
  const entries: Entry[] = [];
  for (const event of events) {
    if (isFlowEvent(event.type)) {
      entries.push({ kind: 'flow', event });
      continue;
    }
    if (rendered.has(event.streamId)) continue;
    rendered.add(event.streamId);
    entries.push({
      kind: 'agent',
      streamId: event.streamId,
      events: eventsByStream.get(event.streamId) ?? [event],
    });
  }
  return entries;
}

function ThreadView({
  events,
  onOpenAgent,
}: {
  events: AxonEvent[];
  onOpenAgent?: (agentId: string) => void;
}) {
  const { containerRef, contentRef, showScrollBtn, onScrollBtnClick } = useAutoScroll();
  const entries = useMemo(() => buildEntries(events), [events]);

  function renderFlowEvent(event: AxonEvent) {
    switch (event.type) {
      case 'flow.started':
        return <FlowStartedBubble key={event.id} event={event} />;
      case 'flow.node_started':
        return <FlowNodeStartedBubble key={event.id} event={event} />;
      case 'flow.node_completed':
        return <FlowNodeCompletedBubble key={event.id} event={event} />;
      case 'flow.node_error':
        return <FlowNodeErrorBubble key={event.id} event={event} />;
      case 'flow.node_retrying':
        return <FlowNodeRetryingBubble key={event.id} event={event} />;
      case 'flow.approval_requested':
        return <FlowApprovalRequestedBubble key={event.id} event={event} />;
      case 'flow.approval_responded':
        return <FlowApprovalRespondedBubble key={event.id} event={event} />;
      case 'flow.completed':
        return <FlowCompletedBubble key={event.id} event={event} />;
      case 'flow.error':
        return <FlowErrorBubble key={event.id} event={event} />;
      case 'flow.cancelled':
        return <FlowCancelledBubble key={event.id} event={event} />;
      default:
        return <EventBubble key={event.id} event={event} />;
    }
  }

  return (
    <div className="relative flex-1 overflow-hidden">
      <div
        ref={containerRef}
        className="h-full overflow-y-auto px-4"
        role="log"
        aria-label="Automation events"
      >
        <div ref={contentRef} className="flex flex-col gap-1 py-4">
          {entries.length === 0 && (
            <EmptyState
              icon={<MessageSquare className="h-5 w-5 text-muted-foreground/60" />}
              title="No events yet"
              description="Events will appear here once the automation starts running"
            />
          )}
          {entries.map((entry) =>
            entry.kind === 'flow' ? (
              renderFlowEvent(entry.event)
            ) : (
              <AgentStreamSummaryBubble
                key={entry.streamId}
                streamId={entry.streamId}
                events={entry.events}
                onOpenAgent={onOpenAgent}
              />
            ),
          )}
        </div>
      </div>

      {showScrollBtn && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
          <Button
            variant="secondary"
            size="icon"
            className="h-8 w-8 rounded-full shadow-md"
            onClick={onScrollBtnClick}
            aria-label="Scroll to bottom"
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

interface FlowRunStreamViewProps {
  events: AxonEvent[];
  isLoading?: boolean;
  /**
   * Called when the user clicks "View Agent" on a stream summary card.
   * Wire this up to the flow run page's per-agent slide-over so the
   * stream view matches the timeline tab's drilldown behaviour.
   */
  onOpenAgent?: (agentId: string) => void;
}

export function FlowRunStreamView({ events, isLoading, onOpenAgent }: FlowRunStreamViewProps) {
  const [view, setView] = useState<StreamView>('thread');

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <EmptyState
          icon={<Radio className="h-5 w-5 text-muted-foreground/60" />}
          title="Loading stream..."
          description="Fetching events from the automation run"
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-end px-3 py-2">
        <ViewToggle view={view} onChange={setView} />
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        {view === 'thread' ? (
          <ThreadView events={events} onOpenAgent={onOpenAgent} />
        ) : (
          <AxonEventList events={events} />
        )}
      </div>
    </div>
  );
}
